import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Alert, Container, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock, faSignInAlt, faCloud, faShieldAlt, faChartLine, faFileAlt, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import './Login.css';
import { Helmet } from 'react-helmet';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  
  // Update document title when component mounts
  useEffect(() => {
    document.title = "Sign In | Document Review System";
  }, []);

  // Validation schema
  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password: Yup.string()
      .required('Password is required')
  });

  // Initial form values
  const initialValues = {
    email: '',
    password: ''
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setError('');
    try {
      const response = await login(values.email, values.password);
      
      if (response.status === 200) {
        // Successful login, redirect to dashboard
        navigate('/dashboard');
      } else if (response.status === 403 && response.data?.verification_required) {
        // Email verification required
        setVerificationRequired(true);
        setPendingEmail(response.data.email);
      } else {
        // Other error
        setError(response.message || 'Login failed');

      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  // Redirect to verification pending page if verification required
  if (verificationRequired && pendingEmail) {
    navigate('/verification-pending', { state: { email: pendingEmail } });
    return null;
  }

  return (
    <div className="login-container">
      <Helmet>
        <title>Sign In | Document Review System</title>
        <meta name="description" content="Secure access to the Document Review System. Analyze, manage, and ensure compliance of your documents with multiple regulatory frameworks." />
        <meta name="keywords" content="document review, compliance, login, document management, regulatory compliance, document analysis" />
      </Helmet>
      
      <div className="login-page-wrapper d-flex min-vh-100">
        {/* Left Panel - Branding and Features */}
        <div className="login-brand-panel d-none d-lg-flex flex-column justify-content-between p-5 text-white">
          <div className="brand-content">
            <div className="brand-logo mb-5 text-center">
              <FontAwesomeIcon icon={faCloud} size="3x" className="mb-3" />
              <h1 className="brand-title fw-bold mb-2">Vcurd Document Review</h1>
              <p className="brand-tagline fs-5 opacity-75">Secure Document Compliance Platform</p>
            </div>
            
            <div className="brand-features mt-5">
              <h3 className="features-title fw-bold mb-3">Document Compliance Made Simple</h3>
              <p className="features-subtitle mb-5">Analyze and ensure compliance across 30+ regulatory frameworks</p>
              
              <div className="feature-list">
                <div className="feature-item d-flex align-items-center mb-4">
                  <div className="feature-icon-wrapper me-3 p-3 rounded-circle">
                    <FontAwesomeIcon icon={faShieldAlt} className="feature-icon" />
                  </div>
                  <div className="feature-text">
                    <h5 className="mb-1">Compliance Assurance</h5>
                    <p className="mb-0 opacity-75">Ensure documents meet ISO, GDPR, HIPAA standards</p>
                  </div>
                </div>
                
                <div className="feature-item d-flex align-items-center mb-4">
                  <div className="feature-icon-wrapper me-3 p-3 rounded-circle">
                    <FontAwesomeIcon icon={faChartLine} className="feature-icon" />
                  </div>
                  <div className="feature-text">
                    <h5 className="mb-1">Advanced Analytics</h5>
                    <p className="mb-0 opacity-75">Detailed compliance reports and insights</p>
                  </div>
                </div>
                
                <div className="feature-item d-flex align-items-center mb-4">
                  <div className="feature-icon-wrapper me-3 p-3 rounded-circle">
                    <FontAwesomeIcon icon={faFileAlt} className="feature-icon" />
                  </div>
                  <div className="feature-text">
                    <h5 className="mb-1">Document Management</h5>
                    <p className="mb-0 opacity-75">Centralized storage with version control</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="brand-footer mt-auto pt-4 opacity-75 text-center">
            <p className="mb-0">© {new Date().getFullYear()} Vcurd Document Review System</p>
          </div>
        </div>
        
        {/* Right Panel - Login Form */}
        <div className="login-form-panel d-flex flex-column justify-content-center p-4 p-md-5">
          <div className="d-block d-lg-none text-center mb-5">
            <FontAwesomeIcon icon={faCloud} size="2x" className="mb-3 text-primary" />
            <h2 className="brand-title-mobile fw-bold">Vcurd Document Review</h2>
            <p className="text-muted">Secure Document Compliance Platform</p>
          </div>
          
          <div className="login-form-container">
            <h2 className="login-title fw-bold mb-4">Sign In</h2>
            <p className="login-subtitle text-muted mb-4">Access your secure portal to analyze and manage document compliance</p>
            
            {error && (
              <Alert variant="danger" className="mb-4">
                {error}
              </Alert>
            )}
            
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form>
                  <div className="form-group position-relative mb-4">
                    <label htmlFor="email" className="form-label fw-medium mb-2">Email Address</label>
                    <div className="input-group-custom position-relative">
                      <FontAwesomeIcon icon={faEnvelope} className="input-icon text-muted" />
                      <Field
                        type="email"
                        id="email"
                        name="email"
                        className="form-control py-3 ps-5"
                        placeholder="Enter your email"
                      />
                    </div>
                    <ErrorMessage name="email" component="div" className="error-message text-danger mt-1 small" />
                  </div>

                  <div className="form-group position-relative mb-4">
                    <label htmlFor="password" className="form-label fw-medium mb-2">Password</label>
                    <div className="input-group-custom position-relative">
                      <FontAwesomeIcon icon={faLock} className="input-icon text-muted" />
                      <Field
                        type="password"
                        id="password"
                        name="password"
                        className="form-control py-3 ps-5"
                        placeholder="Enter your password"
                      />
                    </div>
                    <ErrorMessage name="password" component="div" className="error-message text-danger mt-1 small" />
                  </div>

                  <div className="d-grid gap-2 mt-4 mb-4">
                    <Button 
                      type="submit" 
                      className="login-btn py-3"
                      variant="primary"
                      size="lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Signing in...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faSignInAlt} className="me-2" />
                          Sign In
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <div className="login-footer text-center mt-4">
                    <p className="mb-0">
                      Don't have an account? <Link to="/register" className="text-decoration-none fw-medium">Create Account</Link>
                    </p>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
